import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Sparkles, Loader2, Trash2, Info, AlertTriangle, Clock, CheckCircle2,
  FileEdit, Wand2, XCircle,
} from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { cn } from '../lib/cn';

type IdeaStatus = 'pending' | 'drafting' | 'scheduled' | 'published' | 'failed';

type Idea = {
  id: string;
  title: string;
  targetKeyword: string;
  searchIntent: string;
  funnelStage: string;
  briefAngle: string;
  suggestedCta: string;
  status: IdeaStatus;
  blogPostId: string | null;
  error: string | null;
  createdAt: string;
};

const STATUS_META: Record<IdeaStatus, { label: string; cls: string; Icon: typeof Clock }> = {
  pending:   { label: 'En cola',    cls: 'bg-slate-100 text-slate-600',     Icon: FileEdit },
  drafting:  { label: 'Redactando', cls: 'bg-blue-100 text-blue-700',       Icon: Wand2 },
  scheduled: { label: 'Programado', cls: 'bg-amber-100 text-amber-700',     Icon: Clock },
  published: { label: 'Publicado',  cls: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle2 },
  failed:    { label: 'Error',      cls: 'bg-red-100 text-red-700',         Icon: XCircle },
};

export function AdminAutoblog() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'autoblog-ideas'],
    queryFn: () => api<{ ideas: Idea[] }>('/api/admin/blog/autoblog/ideas'),
    refetchInterval: 20_000, // queue advances in the background
  });
  const ideas = data?.ideas ?? [];

  const counts = ideas.reduce<Record<IdeaStatus, number>>(
    (acc, i) => ({ ...acc, [i.status]: (acc[i.status] ?? 0) + 1 }),
    { pending: 0, drafting: 0, scheduled: 0, published: 0, failed: 0 },
  );

  const generateMutation = useMutation({
    mutationFn: () => api('/api/admin/blog/autoblog/generate-ideas', { method: 'POST' }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'autoblog-ideas'] });
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : 'No se pudieron generar las ideas.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/api/admin/blog/autoblog/ideas/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'autoblog-ideas'] }),
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : 'No se pudo eliminar la idea.'),
  });

  return (
    <div className="space-y-6">
      {/* header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Sparkles size={22} className="text-brand-600" /> Autoblog
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Generación y publicación automática de artículos.
          </p>
        </div>
        <button
          onClick={() => generateMutation.mutate()}
          disabled={generateMutation.isPending}
          className="btn-primary gap-2"
        >
          {generateMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {generateMutation.isPending ? 'Generando ideas…' : 'Generar 25 ideas'}
        </button>
      </div>

      {/* how it works */}
      <div className="flex gap-2.5 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
        <Info size={16} className="text-brand-500 shrink-0 mt-0.5" />
        <p>
          Genera el primer lote de hasta 25 ideas (solo títulos). El sistema redacta cada artículo con IA
          —contenido, resumen, SEO e imagen destacada— y los <strong>publica automáticamente 3 veces al día</strong>{' '}
          (entre 09–10h, 15–16h y 21–22h, con minutos aleatorios). Cuando la cola se vacía,{' '}
          <strong>se genera un lote nuevo automáticamente</strong>, pasándole a la IA los temas ya tratados para
          que no se repita e innove con ángulos distintos.
        </p>
      </div>

      {error && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-center gap-2">
          <AlertTriangle size={15} /> {error}
        </p>
      )}

      {/* counts */}
      {ideas.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {(Object.keys(STATUS_META) as IdeaStatus[]).map((s) => (
            <span
              key={s}
              className={cn('inline-flex items-center gap-1.5 text-xs font-medium rounded-lg px-2.5 py-1.5', STATUS_META[s].cls)}
            >
              {STATUS_META[s].label}: {counts[s]}
            </span>
          ))}
        </div>
      )}

      {/* queue */}
      {isLoading ? (
        <div className="p-12 flex items-center justify-center gap-2 text-slate-400 text-sm">
          <Loader2 size={16} className="animate-spin" /> Cargando cola…
        </div>
      ) : ideas.length === 0 ? (
        <div className="p-12 flex flex-col items-center justify-center gap-3 text-slate-400 border border-dashed border-slate-200 rounded-2xl">
          <Sparkles size={28} />
          <p className="text-sm">No hay ideas en la cola. Genera un lote para empezar.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {ideas.map((idea) => {
            const sm = STATUS_META[idea.status];
            return (
              <div
                key={idea.id}
                className="bg-white border border-slate-200 rounded-xl p-3.5 flex items-start gap-3"
              >
                <span className={cn('inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5 shrink-0 mt-0.5', sm.cls)}>
                  <sm.Icon size={11} className={idea.status === 'drafting' ? 'animate-pulse' : ''} /> {sm.label}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800">{idea.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    <span className="font-medium text-slate-600">{idea.targetKeyword}</span>
                    {idea.funnelStage && <span className="text-slate-400"> · {idea.funnelStage}</span>}
                  </p>
                  {idea.briefAngle && (
                    <p className="text-xs text-slate-400 mt-1 line-clamp-2">{idea.briefAngle}</p>
                  )}
                  {idea.status === 'failed' && idea.error && (
                    <p className="text-xs text-red-600 mt-1 font-mono break-words">{idea.error}</p>
                  )}
                </div>
                {(idea.status === 'pending' || idea.status === 'failed') && (
                  <button
                    onClick={() => deleteMutation.mutate(idea.id)}
                    title="Quitar de la cola"
                    className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 shrink-0"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
